import { bearerAuth, fetchTokenAuth, oauthAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

const urlPattern = () =>
  "https://publicapi\\.challengermode\\.com/(?:graphql(?:\\?.*)?|mk1/v1/.*)$";

const connectionTest = {
  protocol: "http" as const,
  request: () => "https://publicapi.challengermode.com/mk1/v1/me/userinfo",
};

const usageHint = () =>
  "Use https://publicapi.challengermode.com/graphql for the GraphQL Client API and https://publicapi.challengermode.com/mk1/v1/ for REST v1, including games, tournaments, spaces, users, teams, ladders, and leaderboards. REST v1 is deprecated by Challengermode. The proxy injects Bearer authentication for both APIs. BOT and PAT permissions differ by operation. HMAC authenticates incoming webhooks, not outgoing API calls; validate incoming signatures, body checksums, timestamps, and replay protection in the receiving workflow.";

export const challengermode = defineProduct({
  name: "Challengermode",
  tagLine: "Competitions, tournaments, matches, and player data",
  logo,
  tags: ["data"],
  variants: [
    productVariant({
      identifier: "challengermode",
      authLabel: "Bot refresh key",
      protocol: "http",
      defaultUrlPattern: urlPattern,
      auth: (app) =>
        fetchTokenAuth({
          request: {
            url: "https://publicapi.challengermode.com/mk1/v1/auth/access_keys",
            method: "POST",
            jsonBody: { refreshKey: app.secret.refreshKey },
          },
          extract: { from: "jsonPath", paths: ["value"] },
          apply: { kind: "header", scheme: "Bearer" },
        }),
      steps: [
        {
          title: "Create a bot refresh key",
          items: [
            {
              kind: "markdown",
              content:
                "Open your application in the [Challengermode application dashboard](https://www.challengermode.com/developers/applications). In **Credentials**, create a long-lived refresh key. To manage a Space, link the application’s bot to a Space you administer. The connector exchanges the refresh key for a short-lived bot access token automatically. Both GraphQL and REST v1 use this token.",
            },
            {
              kind: "input",
              label: "Refresh key",
              required: true,
              target: { kind: "secrets", field: "refreshKey" },
            },
          ],
        },
      ],
      connectionTest,
      usageHint,
    }),
    productVariant({
      identifier: "challengermodeOAuth",
      authLabel: "OAuth",
      protocol: "http",
      defaultUrlPattern: urlPattern,
      auth: oauthAuth,
      steps: [
        {
          title: "Configure a user OAuth client",
          items: [
            {
              kind: "markdown",
              content:
                "Create an OAuth client in your application’s **Credentials** tab in the [application dashboard](https://www.challengermode.com/developers/applications). Register the redirect URL below. PKCE is always enabled, and offline access allows the connector to refresh personal access tokens automatically. Choose the client authentication method configured for your application.",
            },
            {
              kind: "copyable",
              label: "Redirect URL",
              value:
                "{API_URL}/connected-apps/oauth/challengermodeOAuth/callback",
            },
            {
              kind: "select",
              label: "Client authentication",
              defaultValue: "client_secret_post",
              target: { kind: "configuration", field: "tokenAuthMethod" },
              options: [
                {
                  value: "client_secret_post",
                  label: "Client secret in request body",
                },
                {
                  value: "client_secret_basic",
                  label: "Client secret using HTTP Basic",
                },
                { value: "private_key_jwt", label: "Private key JWT" },
                { value: "none", label: "Public client with PKCE" },
              ],
            },
            {
              kind: "input",
              label: "Client ID",
              required: true,
              target: { kind: "oauthConnection", field: "clientId" },
            },
            {
              kind: "input",
              label: "Client secret or PEM private key",
              target: { kind: "oauthConnection", field: "clientSecret" },
              helperText:
                "Enter the client secret, or the PEM private key for private key JWT. Leave blank for a public client.",
              visibleWhen: [
                {
                  target: { kind: "configuration", field: "tokenAuthMethod" },
                  notValue: "none",
                },
              ],
            },
            {
              kind: "input",
              label: "Signing key ID",
              target: { kind: "configuration", field: "keyId" },
              visibleWhen: [
                {
                  target: { kind: "configuration", field: "tokenAuthMethod" },
                  value: "private_key_jwt",
                },
              ],
            },
            {
              kind: "input",
              label: "Scopes",
              defaultValue: "openid offline_access",
              target: { kind: "oauthConnection", field: "scopes" },
            },
            {
              kind: "oauth",
              dependsOn: [
                { kind: "oauthConnection", field: "clientId" },
                { kind: "configuration", field: "tokenAuthMethod" },
              ],
            },
          ],
        },
      ],
      oauth: {
        grantType: () => "authorization_code",
        authorizationEndpoint: () =>
          "https://www.challengermode.com/oauth/authorize",
        tokenEndpoint: () => "https://www.challengermode.com/oauth/token",
        tokenEndpointAuthMethod: ({ tokenAuthMethod }) => {
          switch (tokenAuthMethod) {
            case "client_secret_basic":
            case "private_key_jwt":
            case "none":
              return tokenAuthMethod;
            default:
              return "client_secret_post";
          }
        },
        clientAssertion: ({ keyId }) => ({ keyId }),
        clientCredentialsFrom: "db",
        scopes: () => ["openid", "offline_access"],
        pkce: () => true,
        issuer: () => "https://challengermode.com/",
      },
      connectionTest,
      usageHint,
    }),
    productVariant({
      identifier: "challengermodeAccessToken",
      authLabel: "Existing BOT or PAT access token",
      protocol: "http",
      defaultUrlPattern: urlPattern,
      auth: (app) => bearerAuth(app.secret.accessToken),
      steps: [
        {
          title: "Enter an existing access token",
          items: [
            {
              kind: "markdown",
              content:
                "Use an existing BOT or PAT access token, including a PAT issued by a device code or partner assertion flow. This option does not renew tokens: replace the token before it expires. For unattended workflows, choose **Bot refresh key** or **OAuth** instead. The token works with both GraphQL and REST v1, subject to its permissions.",
            },
            {
              kind: "input",
              label: "Access token",
              required: true,
              target: { kind: "secrets", field: "accessToken" },
            },
          ],
        },
      ],
      connectionTest,
      usageHint,
    }),
  ],
});
